import { getApiErrorStatus } from "_shared/_errors/api-error-message";

/**
 * Which request failed, because the honest answer differs between the two.
 *
 * A cancel that does not arrive has changed nothing and can be sent again. A confirm that does
 * not arrive may already have written, so the same failure means something else entirely.
 */
export enum StagedActionIntent {
  Confirm,
  Cancel,
}

/** What a confirm or a cancel that never reached an answer leaves on the card. */
export interface StagedActionResolutionFailure {
  /**
   * What the user is told.
   *
   * Written here, never taken from the server's error body. A permission or validation message
   * can name records the user does not know exist, which is the same reason
   * `StagedAssistantAction` records an exception's type name and never its message.
   */
  readonly text: string;
  /**
   * Whether the card keeps its controls.
   *
   * False wherever a second click could double a write or would certainly be refused again.
   */
  readonly canRetry: boolean;
}

/**
 * What to say about a confirm or a cancel that produced no answer.
 *
 * A refusal is not a failure and does not come through here: the server answers "that expired",
 * "you already approved that one" and every other late click with 200 and prose, and the card is
 * redrawn in the state that travels with it. What reaches this function is a request that failed
 * to produce an answer at all — a 403, a dropped connection, a timeout, a 500.
 *
 * The distinction the whole function exists for is **failed** against **uncertain**. A confirm
 * whose response never arrived may have been received and executed: the server writes
 * `Executing` durably before the work runs, so the outcome is genuinely unknown to everyone.
 * Telling the user it failed is as wrong as telling them it worked, and re-offering Confirm
 * invites them to approve work that may already be done. So an uncertain confirm says so and
 * keeps its controls off. A cancel in the same position has written nothing — the preview is at
 * worst still pending — so it says so and offers the button again.
 */
export function describeResolutionFailure(
  error: unknown,
  intent: StagedActionIntent
): StagedActionResolutionFailure {
  const status = getApiErrorStatus(error);

  // The server answers both a foreign action and one that does not exist with 403, so that a
  // caller cannot learn an id exists by asking about it. Either way this one is not answerable.
  if (status === 403) {
    return {
      text: "That is no longer yours to answer. Ask me again and I will prepare it afresh.",
      canRetry: false,
    };
  }

  if (status === 401) {
    return {
      text: "Your session has expired, so nothing was done. Sign in again and ask me to prepare it afresh.",
      canRetry: false,
    };
  }

  // A request the server rejected before acting on it. Clicking again sends the same request.
  if (status === 400 || status === 422) {
    return {
      text: "That request was not accepted, so nothing was changed. Ask me again and I will prepare it afresh.",
      canRetry: false,
    };
  }

  // Refused before it reached the handler, so it cannot have been applied. Safe to send again.
  if (status === 429) {
    return {
      text: "There were too many requests just then, so nothing was done. You can try again in a moment.",
      canRetry: true,
    };
  }

  if (intent === StagedActionIntent.Cancel) {
    return {
      text: "I could not cancel that just then. Nothing has been done, so it is safe to try again.",
      canRetry: true,
    };
  }

  return {
    text: "I could not tell whether that went through. Check before approving it again, and ask me if you are not sure.",
    canRetry: false,
  };
}
